import { useEffect, useState } from "react";
import { fetchClassSubjects } from "../../academicOperations/services/classSubjectsService";
import { ClassSubjectsData } from "../../academicOperations/types";
import { createFaculty, getFacultyForm, updateFaculty } from "../service/facultyService";
import { FacultyAssignment, FacultyFormRequest } from "../types";

export type FacultyDetails = Omit<FacultyFormRequest, "assignments">;
export type FacultyFieldErrors = Partial<Record<keyof FacultyDetails, string>>;

export const emptyDetails: FacultyDetails = { name: "", gender: "Male", phone: "", email: "" };

// Same rules the backend enforces (400).
const PHONE = /^[6-9]\d{9}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const assignmentKey = (a: FacultyAssignment) => `${a.gradeSectionId}|${a.subjectCode}`;
const fromKey = (key: string): FacultyAssignment => {
  const [gradeSectionId, subjectCode] = key.split("|");
  return { gradeSectionId: Number(gradeSectionId), subjectCode };
};

// The backend sends conflict/not-found reasons as a plain-text body.
const messageOf = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  return typeof data === "string" && data.trim() ? data : fallback;
};

export const validateDetails = (details: FacultyDetails): FacultyFieldErrors => {
  const errors: FacultyFieldErrors = {};
  if (!details.name.trim()) errors.name = "Name is required.";
  if (!details.phone) errors.phone = "Phone is required.";
  else if (!PHONE.test(details.phone)) errors.phone = "Enter a valid 10-digit mobile number.";
  if (details.email.trim() && !EMAIL.test(details.email.trim())) errors.email = "Enter a valid email.";
  return errors;
};

/**
 * Add / Edit Faculty. `facultyId` undefined = create. Loads the class tree (GET /class-subjects)
 * and, in edit mode, the faculty (GET /faculties/{id}/form). Ticks are kept as a set of
 * "gradeSectionId|subjectCode" keys.
 */
export const useFacultyForm = (facultyId?: number) => {
  const [tree, setTree] = useState<ClassSubjectsData>({ subjects: [], grades: [] });
  const [details, setDetails] = useState<FacultyDetails>(emptyDetails);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [loginId, setLoginId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [classSubjects, faculty] = await Promise.all([
          fetchClassSubjects(),
          facultyId ? getFacultyForm(facultyId) : Promise.resolve(null),
        ]);
        setTree(classSubjects);
        if (faculty) {
          setDetails({ name: faculty.name, gender: faculty.gender, phone: faculty.phone, email: faculty.email });
          setTicked(new Set(faculty.assignments.map(assignmentKey)));
          setLoginId(faculty.loginId);
        } else {
          setDetails(emptyDetails);
          setTicked(new Set());
          setLoginId(null);
        }
      } catch (err: any) {
        setLoadError(
          err?.response?.status === 404 ? "Faculty not found." : messageOf(err, "Failed to load the form.")
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [facultyId]);

  /** Ticks or unticks all of `keys`: if every one is ticked they're unticked, otherwise all are ticked. */
  const toggleAll = (keys: string[]) => {
    setTicked((prev) => {
      const next = new Set(prev);
      const allTicked = keys.length > 0 && keys.every((key) => prev.has(key));
      keys.forEach((key) => (allTicked ? next.delete(key) : next.add(key)));
      return next;
    });
  };

  const reset = () => {
    setDetails(emptyDetails);
    setTicked(new Set());
  };

  /** Returns null on success, or the reason it failed. */
  const save = async (): Promise<string | null> => {
    const request: FacultyFormRequest = {
      name: details.name.trim(),
      gender: details.gender,
      phone: details.phone,
      email: details.email.trim(),
      assignments: Array.from(ticked).map(fromKey),
    };
    setSaving(true);
    try {
      const saved = facultyId ? await updateFaculty(facultyId, request) : await createFaculty(request);
      setTicked(new Set(saved.assignments.map(assignmentKey)));
      setLoginId(saved.loginId);
      return null;
    } catch (err) {
      return messageOf(err, facultyId ? "Failed to update faculty." : "Failed to create faculty.");
    } finally {
      setSaving(false);
    }
  };

  return {
    tree,
    details,
    setDetails,
    ticked,
    toggleAll,
    loginId,
    loading,
    saving,
    loadError,
    reset,
    save,
  };
};
